import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  X,
  Save,
  Clock,
  Tag,
  Plus,
  Trash2,
  AlertCircle,
  Edit3
} from 'lucide-react';
import { MediaPreview, MediaUpdateRequest } from '../../types/api';
import { useMidias, useKeywordSuggestions } from '../../hooks/useMidias';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ModalContent = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 650px;
  width: 100%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-height: 95vh;
    max-width: 95vw;
  }
`;

const ModalHeader = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing[2]};
  overflow-y: auto;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const ModalFooter = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${({ $variant, theme }) => {
    if ($variant === 'primary') {
      return `
        background: ${theme.colors.primary};
        color: ${theme.colors.textPrimary};
        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: ${theme.shadows.neon};
        }
      `;
    }
    return `
      background: ${theme.colors.backgroundSecondary};
      color: ${theme.colors.textSecondary};
      border-color: ${theme.colors.border};
      &:hover {
        background: ${theme.colors.backgroundTertiary};
        color: ${theme.colors.textPrimary};
        border-color: ${theme.colors.primary};
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  ${({ $required, theme }) => $required ? `&::after { content: ' *'; color: ${theme.colors.error}; }` : ''}
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  width: 100%;
  resize: vertical;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const FileInfo = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const FileName = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const FileType = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: capitalize;
`;

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[1]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const DelayInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const DelayInput = styled.input`
  width: 80px;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  /* Remove as setas do input number */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
    outline: none;
  }
`;

const DelayUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const KeywordInputContainer = styled.div`
  position: relative;
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const SuggestionItem = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  text-align: left;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const KeywordTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const KeywordTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const RemoveKeywordButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const IntentionsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const IntentionsTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 4px 0;
`;

const IntentionGroup = styled.div`
  margin-bottom: 6px;
`;

const IntentionLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: block;
`;

const IntentionKeywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ClickableKeyword = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme }) => `${theme.colors.primary}15`};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}30`};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => `${theme.colors.primary}25`};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const NewKeywordInput = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

interface MediaEditProps {
  media: MediaPreview;
  userId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const MediaEdit: React.FC<MediaEditProps> = ({ media, userId, onClose, onSuccess }) => {
  const { updateMediaConfig, isUpdating } = useMidias(userId);
  const { getSuggestions, intentions } = useKeywordSuggestions(userId);

  const [palavrasChave, setPalavrasChave] = useState<string[]>(media.palavrasChave);
  const [descricao, setDescricao] = useState(media.descricao);
  const [delaySegundos, setDelaySegundos] = useState(media.delaySegundos);
  const [newKeyword, setNewKeyword] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestions = getSuggestions(newKeyword);

  const addKeyword = (keyword: string) => {
    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword && !palavrasChave.includes(trimmedKeyword)) {
      setPalavrasChave([...palavrasChave, trimmedKeyword]);
    }
    setNewKeyword('');
    setShowSuggestions(false);
  };

  const removeKeyword = (index: number) => {
    setPalavrasChave(palavrasChave.filter((_, i) => i !== index));
  };

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newKeyword.trim()) {
      e.preventDefault();
      addKeyword(newKeyword);
    }
  };

  const addKeywordFromSuggestion = (keyword: string) => {
    if (!palavrasChave.includes(keyword)) {
      setPalavrasChave([...palavrasChave, keyword]);
    }
  };

  const canSubmit = palavrasChave.length > 0 && !isUpdating && Boolean(userId);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (palavrasChave.length === 0) {
      setError('Pelo menos uma palavra-chave é obrigatória');
      return;
    }

    if (!userId) {
      setError('É necessário estar autenticado para atualizar mídias.');
      return;
    }

    setError(null);

    const request: MediaUpdateRequest = {
      palavras_chave: palavrasChave.join(', '),
      descricao: descricao.trim() || undefined,
      delay_segundos: delaySegundos > 0 ? delaySegundos : undefined,
      userId,
    };

    updateMediaConfig(
      { filename: media.fileName, config: request },
      {
        onSuccess: () => {
          onSuccess?.();
        },
      }
    );
  };

  const hasChanges =
    JSON.stringify(palavrasChave.sort()) !== JSON.stringify(media.palavrasChave.sort()) ||
    descricao !== media.descricao ||
    delaySegundos !== media.delaySegundos;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Edit3 size={16} />
            Editar Mídia
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={14} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>

      <FileInfo>
        <FileName>{media.fileName}</FileName>
        <FileType>{media.tipo === 'video' ? 'Vídeo' : 'Imagem'}</FileType>
      </FileInfo>

      <FormSection>
        <Label $required>Palavras-chave</Label>
        <KeywordTags>
          {palavrasChave.map((keyword, index) => (
            <KeywordTag key={index}>
              <Tag size={12} />
              {keyword}
              <RemoveKeywordButton onClick={() => removeKeyword(index)}>
                <X size={12} />
              </RemoveKeywordButton>
            </KeywordTag>
          ))}
        </KeywordTags>

        <NewKeywordInput>
          <KeywordInputContainer style={{ flex: 1 }}>
            <Input
              placeholder="Adicionar nova palavra-chave..."
              value={newKeyword}
              onChange={(e) => {
                setNewKeyword(e.target.value);
                setShowSuggestions(e.target.value.length > 2);
              }}
              onFocus={() => setShowSuggestions(newKeyword.length > 2)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyPress={handleKeywordInputKeyPress}
            />
            <AnimatePresence>
              {showSuggestions && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <SuggestionsContainer>
                    {suggestions.map((suggestion, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => addKeyword(suggestion)}
                      >
                        {suggestion}
                      </SuggestionItem>
                    ))}
                  </SuggestionsContainer>
                </motion.div>
              )}
            </AnimatePresence>
          </KeywordInputContainer>
          <Button
            $variant="secondary"
            onClick={() => addKeyword(newKeyword)}
            disabled={!newKeyword.trim()}
          >
            <Plus size={16} />
          </Button>
        </NewKeywordInput>
      </FormSection>

      <IntentionsSection>
        <IntentionsTitle>Sugestões por intenção (clique para adicionar):</IntentionsTitle>
        {Object.entries(intentions).map(([intention, keywords]) => (
          <IntentionGroup key={intention}>
            <IntentionLabel>{intention}</IntentionLabel>
            <IntentionKeywords>
              {keywords.slice(0, 6).map((keyword, index) => (
                <ClickableKeyword
                  key={index}
                  onClick={() => addKeywordFromSuggestion(keyword)}
                  style={{
                    opacity: palavrasChave.includes(keyword) ? 0.5 : 1,
                    cursor: palavrasChave.includes(keyword) ? 'default' : 'pointer'
                  }}
                >
                  {palavrasChave.includes(keyword) ? (
                    <span>✓ {keyword}</span>
                  ) : (
                    <>
                      <Plus size={10} />
                      {keyword}
                    </>
                  )}
                </ClickableKeyword>
              ))}
            </IntentionKeywords>
          </IntentionGroup>
        ))}
      </IntentionsSection>

      <FormSection>
        <FormGrid>
          <div>
            <Label>Descrição (opcional)</Label>
            <Textarea
              rows={3}
              placeholder="Descrição da mídia para facilitar identificação..."
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          <div>
            <Label>Delay de envio</Label>
            <DelayInputContainer>
              <Clock size={16} />
              <DelayInput
                type="number"
                min="1"
                max="60"
                value={delaySegundos}
                onChange={(e) => setDelaySegundos(Number(e.target.value))}
              />
              <DelayUnit>segundos</DelayUnit>
            </DelayInputContainer>
          </div>
        </FormGrid>
      </FormSection>

      {error && (
        <ErrorMessage>
          <AlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            $variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || !hasChanges}
          >
            <Save size={16} />
            {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MediaEdit;